import React from 'react';
import UserProfileEntry from '~store/Profile/UserProfileEntry';
import { IndoorGeofencingMode, OutdoorGeofencingMode, ZoneEntry } from '@ixicare/resources';
import { t } from '~i18n';
import { useZones } from '~hooks/zone';
import { useProfileConfiguration } from '~utils/profile';
import styles from './ResidentConfigDetail.module.css';
import { Spinner } from '@ixicare/ui';

export interface ResidentConfigDetailProps {
  profile: UserProfileEntry;
}

export const ResidentConfigDetail: React.FC<ResidentConfigDetailProps> = (props) => {
  const query = useProfileConfiguration(props.profile.id);
  const siteUuid = props.profile.site?.uuid;
  const allZones = useZones({ siteUuid });

  // Read exactly as stored, with the same defaults the form applies, so the two views cannot
  // describe one configuration differently. Reported as stored on purpose - no device correction
  // here: a Lite carrying a legacy perimeter should show it rather than have it quietly hidden.
  const indoorEnabled = query.data?.['indoor-geofencing-enabled'] ?? false;
  const polarity = query.data?.['indoor-geofencing-mode'] ?? IndoorGeofencingMode.Allow;
  const outdoorMode = query.data?.['outdoor-geofencing-mode'] ?? OutdoorGeofencingMode.None;

  const indoorGeofenceZones: ZoneEntry[] = React.useMemo(
    () =>
      (query.data?.['indoor-geofencing-zones'] ?? [])
        .map((uuid) => allZones.find((z) => z.uuid === uuid))
        .filter((z): z is ZoneEntry => !!z),
    [query.data, allZones]
  );

  if (query.isLoading) {
    return <Spinner />;
  }

  return (
    <div>
      <div className="row">
        {props.profile.ownedDevice && (
          <div className="col-12 col-md-6">
            <p className={styles.heading}>{t('profile-configuration.detail.device')}</p>
            <div className={styles.block}>
              <p className={styles.label}>{t('devices.table.macAddress')}</p>
              <p className={styles.value}>{props.profile.ownedDevice?.macAddress}</p>
            </div>
            <div className={styles.block}>
              <p className={styles.label}>{t('devices.table.deviceType')} </p>
              <p className={styles.value}>
                {t(`device.types.${props.profile.ownedDevice?.deviceType?.toLowerCase()}`)}
              </p>
            </div>
            {props.profile.ownedDevice.phoneNumber && (
              <div className={styles.block}>
                <p className={styles.label}>{t('alert.call.devicePhoneNumber')}</p>
                <p className={styles.value}>{props.profile.ownedDevice.phoneNumber}</p>
              </div>
            )}
          </div>
        )}
        <div className="col-12 col-md-6">
          <p className={styles.heading}>{t('profile-configuration.detail.settings')}</p>
          <div className={styles.block}>
            <p className={styles.label}>{t('profile-configuration.indoor-checkin-frequency.label')}</p>
            <p className={styles.value}>{query.data?.['indoor-checkin-frequency']}s</p>
          </div>
          <div className={styles.block}>
            {!indoorEnabled && outdoorMode === OutdoorGeofencingMode.None && (
              <>
                <p className={styles.label}>{t('profile-configuration.geofence.label')}</p>
                <p className={styles.value}>{t('profile-configuration.geofence.off')}</p>
              </>
            )}
            {indoorEnabled && (
              <>
                <p className={styles.label}>{t('profile-configuration.geofence.indoor.title')}</p>
                <p className={styles.value}>{t(`profile-configuration.geofence.indoor.${polarity}.title`)}</p>
                {indoorGeofenceZones.length > 0 && (
                  <div>
                    <p className={styles.label}>{t(`profile-configuration.geofence.indoor.${polarity}.zones`)}</p>
                    {indoorGeofenceZones.map((zone) => (
                      <p key={zone.uuid} className={styles.value}>
                        {zone.name}
                      </p>
                    ))}
                  </div>
                )}
              </>
            )}
            {outdoorMode !== OutdoorGeofencingMode.None && (
              <>
                <p className={styles.label}>{t('profile-configuration.geofence.outdoor.title')}</p>
                <p className={styles.value}>{t(`profile-configuration.geofence.outdoor.${outdoorMode}.title`)}</p>
                {outdoorMode === OutdoorGeofencingMode.Geo && (
                  <p className={styles.disclaimer}>{t('profile-configuration.outdoor-geofencing-geo.disclaimer')}</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
